import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Spinner, Image, Tabs, Tab } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router-dom';
import {
  createSession, createUserJourney, uploadImage, processImage,
  processImageStep1, processImageQuickAnalysis, getContextBasedQuestions, saveConversation, completeImageProcessing,
  getSession, getUserJourneys, getImage, getImages, chatWithImage, regenerateWithFeedback, addDomainToUser
} from '../services/firebaseService';
import { getValidAccessToken } from '../services/authService';
import { UserJourney, ImageData, Conversation, PointData, UserSession } from '../types';

const SESSIONS_API_BASE = `${process.env.REACT_APP_URL}/chat_application/v1/sessions`;
const CHAT_FEEDBACK_API_URL = `${process.env.REACT_APP_URL}/chat_application/v1/image-feedback`;
const TOOL_ID = process.env.REACT_APP_TOOL_ID;

/**
 * GET /sessions/{session_id}/journeys — Bearer access_token.
 * Returns null if the request cannot be used (no token or non-OK response).
 */
const fetchJourneysFromApi = async (sid: string): Promise<UserJourney[] | null> => {
  const accessToken = await getValidAccessToken();
  if (!accessToken) return null;

  const url = `${SESSIONS_API_BASE}/${encodeURIComponent(sid)}/journeys`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) return null;

  const data = await response.json();
  const raw = Array.isArray(data?.user_journeys)
    ? data.user_journeys
    : Array.isArray(data?.journeys)
      ? data.journeys
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : [];

  return raw
    .filter((item: any) => item && (item.user_journey_id || item.journey_id || item.id))
    .map((item: any) => ({
      id: String(item.user_journey_id ?? item.journey_id ?? item.id),
      journeyName: item.user_journey_name ?? item.journey_name ?? item.journeyName ?? 'Journey',
      sessionId: String(item.session_id ?? data?.session_id ?? sid),
      createdAt: item.created_at ? new Date(item.created_at) : new Date(),
      createdBy: item.created_by ?? '',
    }));
};

/**
 * GET /sessions/{session_id}/journeys/{journey_id} when a journey is selected.
 * Returns mapped images for the selected journey.
 */
const fetchSelectedJourneyDetails = async (sid: string, jid: string): Promise<ImageData[] | null> => {
  const accessToken = await getValidAccessToken();
  if (!accessToken) return null;

  const url = `${SESSIONS_API_BASE}/${encodeURIComponent(sid)}/journeys/${encodeURIComponent(jid)}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    const data = await response.json();
    const rawImages = Array.isArray(data?.images) ? data.images : [];

    return rawImages
      .filter((item: any) => item && (item.image_id || item.id))
      .map((item: any) => {
        const darkPatterns = Array.isArray(item.dark_patterns) ? item.dark_patterns : [];
        const mappedResponse = darkPatterns.map((dp: any) => ({
          text: dp.dark_pattern_type
            ? `${dp.dark_pattern_type}: ${dp.dark_pattern_description || 'No description provided'}`
            : (dp.dark_pattern_description || 'No description provided'),
          kind: dp.dark_pattern_type || 'Unknown',
          type: 'outcome',
          laws: [],
        }));

        return {
          id: String(item.image_id ?? item.id),
          sessionId: String(item.session_id ?? sid),
          journeyId: String(item.user_journey_id ?? jid),
          imageUrl: item.image_url ?? '',
          url: item.webpage_url ?? '',
          note: item.page_name ?? undefined,
          createdAt: item.created_at ? new Date(item.created_at) : new Date(),
          status: item.status ?? 'processing',
          feedback: item.img_feedback ?? undefined,
          response: mappedResponse,
        } as ImageData;
      });
  } catch (error) {
    console.error('Error fetching selected journey details:', error);
    return null;
  }
};

const postUserChatFeedback = async (imgId: string, textFeedback: string): Promise<void> => {
  const accessToken = await getValidAccessToken();

  if (!imgId || !textFeedback.trim()) return;

  await fetch(CHAT_FEEDBACK_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      image_id: imgId,
      text_feedback: textFeedback.trim(),
    }),
  });
};

interface ChatPanelProps {
  userEmail: string;
  userDomains: string[];
  onLogout: () => void;
  userSession?: UserSession; // Add user session for proper organization context
}

// Predefined user journeys
const PREDEFINED_JOURNEYS = [
  'Landing page',
  'Account creation',
  'Product page',
  'Purchasing journey',
  'Subscription journey',
  'Cookie banner',
  'Personal data preferences',
  'Exercise of personal data rights'
];

// Component for rendering compliance issues with expandable laws
const ComplianceIssueCard: React.FC<{
  point: any;
  index: number;
  messageId: string;
  expandedLaws: { [key: string]: boolean };
  toggleLawExpansion: (key: string) => void;
}> = ({ point, index, messageId, expandedLaws, toggleLawExpansion }) => {
  const hasLaws = point.laws && point.laws.length > 0;
  const lawKey = `${messageId}-${index}`;
  const isExpanded = expandedLaws[lawKey] || false;

  return (
    <div
      className="compliance-issue-card"
      style={{
        border: '1px solid #d7d4ff',
        borderRadius: '10px',
        padding: '16px 20px',
        marginBottom: '12px',
        backgroundColor: 'white',
        position: 'relative'
      }}
    >
      {/* Issue Number and Toggle */}
      <div className="d-flex align-items-start justify-content-between">
        <div className="d-flex align-items-start gap-3 flex-grow-1">
          <span style={{
            fontFamily: 'Museo Sans',
            fontWeight: 700,
            color: '#475467',
            fontSize: '17px',
            lineHeight: '17px'
          }}>
            {index + 1}.
          </span>
          <p style={{
            color: '#475467',
            fontSize: '14px',
            margin: 0,
            flex: 1
          }}>
            {point.text}
          </p>
        </div>

        {/* Expand/Collapse Icon */}
        {hasLaws && (
          <button
            onClick={() => toggleLawExpansion(lawKey)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              marginLeft: '12px',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <i
              className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
              style={{ fontSize: '16px', color: '#475467' }}
            ></i>
          </button>
        )}
      </div>

      {/* Laws Table - Expanded View with Fixed Position */}
      {hasLaws && isExpanded && (
        <div className="laws-table mt-3" style={{
          border: '1px solid #d0d0ce',
          borderRadius: '14px',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 10,
          backgroundColor: 'white',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Fixed Header */}
          <div style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#d7d4ff',
            borderBottom: '1px solid #d0d0ce',
            zIndex: 11
          }}>
            <div className="d-flex" style={{ fontSize: '11px' }}>
              <div style={{
                padding: '10px',
                fontWeight: 700,
                color: '#1d252c',
                flex: '0 0 25%',
                borderRight: '1px solid #d0d0ce'
              }}>
                Applicable law
              </div>
              <div style={{
                padding: '10px',
                fontWeight: 700,
                color: '#1d252c',
                flex: '0 0 20%',
                borderRight: '1px solid #d0d0ce'
              }}>
                Clause
              </div>
              <div style={{
                padding: '10px',
                fontWeight: 700,
                color: '#1d252c',
                flex: '0 0 35%',
                borderRight: '1px solid #d0d0ce'
              }}>
                Explanation
              </div>
              <div style={{
                padding: '10px',
                fontWeight: 700,
                color: '#1d252c',
                flex: '0 0 20%'
              }}>
                Source
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {point.laws.map((law: any, lawIdx: number) => (
              <div key={lawIdx} className="d-flex" style={{
                fontSize: '11px',
                borderBottom: lawIdx < point.laws.length - 1 ? '1px solid #d0d0ce' : 'none'
              }}>
                <div style={{
                  padding: '12px',
                  fontWeight: 700,
                  color: '#1d252c',
                  flex: '0 0 25%',
                  borderRight: '1px solid #d0d0ce'
                }}>
                  {law.law_name}
                </div>
                <div style={{
                  padding: '12px',
                  color: '#1d252c',
                  flex: '0 0 20%',
                  borderRight: '1px solid #d0d0ce'
                }}>
                  {law.clause_name || 'N/A'}
                </div>
                <div style={{
                  padding: '12px',
                  color: '#1d252c',
                  flex: '0 0 35%',
                  borderRight: '1px solid #d0d0ce'
                }}>
                  {law.description || 'No description available'}
                </div>
                <div style={{
                  padding: '12px',
                  color: '#1d252c',
                  flex: '0 0 20%'
                }}>
                  {law.link ? (
                    <a
                      href={law.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: '#7752FF',
                        fontWeight: 600
                      }}
                    >
                      View Law
                    </a>
                  ) : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChatPanel: React.FC<ChatPanelProps> = ({ userEmail, userDomains, onLogout, userSession }) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const location = useLocation();
  const navSessionName = (location.state as { sessionName?: string } | null | undefined)?.sessionName;
  const [sessionName, setSessionName] = useState('');
  const [domainName, setDomainName] = useState('');
  const [isAddingNewDomain, setIsAddingNewDomain] = useState(false);
  const [newDomainInput, setNewDomainInput] = useState('');
  // Initialize domains from prop or localStorage
  const getInitialDomains = (): string[] => {
    if (userDomains && userDomains.length > 0) {
      return userDomains;
    }
    // Try to load from localStorage as fallback
    const storedDomains = localStorage.getItem('userDomains');
    if (storedDomains) {
      try {
        const parsedDomains = JSON.parse(storedDomains);
        if (Array.isArray(parsedDomains) && parsedDomains.length > 0) {
          return parsedDomains;
        }
      } catch (error) {
        console.error('Error parsing stored domains:', error);
      }
    }
    return userDomains || [];
  };
  const [localUserDomains, setLocalUserDomains] = useState<string[]>(getInitialDomains());
  const [journeys, setJourneys] = useState<UserJourney[]>([]);
  const [selectedJourney, setSelectedJourney] = useState<string | null>(null);
  const [journeyName, setJourneyName] = useState('');
  const [isCreatingJourney, setIsCreatingJourney] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [note, setNote] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentImage, setCurrentImage] = useState<ImageData | null>(null);
  const [journeyImages, setJourneyImages] = useState<ImageData[]>([]);
  const [activeTab, setActiveTab] = useState('analyze');
  const [screenCapture, setScreenCapture] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [useConversationMode] = useState(false);
  const [fetchLaws, setFetchLaws] = useState(true); // Default to true (active)
  const [currentQuestions, setCurrentQuestions] = useState<string[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [expandedLaws, setExpandedLaws] = useState<{ [key: string]: boolean }>({});

  const [messages, setMessages] = useState<Array<{
    id: string;
    type: 'system' | 'user' | 'image' | 'response' | 'question';
    content: string;
    imageUrl?: string;
    points?: PointData[];
    className?: string;
    journeyName?: string;
  }>>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const [chatQuestion, setChatQuestion] = useState('');
  const [refineFeedback, setRefineFeedback] = useState('');
  const [showJourneyDropdown, setShowJourneyDropdown] = useState(false);
  const [showPredefinedDropdown, setShowPredefinedDropdown] = useState(false);
  const [showUserJourneyDropdown, setShowUserJourneyDropdown] = useState(false);

  const generateUniqueId = (prefix: string): string => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Handle expanding/collapsing law tables
  const toggleLawExpansion = (lawKey: string) => {
    setExpandedLaws(prev => ({ ...prev, [lawKey]: !prev[lawKey] }));
  };


  const loadSessionData = useCallback(async () => {
    try {
      if (!sessionId) return;

      const session = await getSession(sessionId);
      const displayName = session?.sessionName ?? navSessionName ?? 'Untitled Session';
      if (session) {
        setSessionName(session.sessionName);
      } else {
        setSessionName(displayName);
      }

      const apiJourneys = await fetchJourneysFromApi(sessionId);
      const userJourneys =
        apiJourneys !== null ? apiJourneys : await getUserJourneys(sessionId);

      console.log('Loaded user journeys:', userJourneys);
      setJourneys(userJourneys);

      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('session-loaded'),
          type: 'system',
          content: `Session "${displayName}" loaded. ${userJourneys.length > 0
            ? 'Select a user journey or create a new one.'
            : 'Create a new user journey to start.'
            }`
        }
      ]);
    } catch (error) {
      console.error('Error loading session data:', error);
    }
  }, [sessionId, navSessionName]);

  const loadJourneyImages = useCallback(async () => {
    try {
      if (!sessionId || !selectedJourney) return;

      const apiImages = await fetchSelectedJourneyDetails(sessionId, selectedJourney);
      const images =
        apiImages !== null ? apiImages : await getImages(sessionId, selectedJourney);

      // Sort images by creation date (newest first)
      images.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setJourneyImages(images);
    } catch (error) {
      console.error('Error loading journey images:', error);
    }
  }, [sessionId, selectedJourney]);

  useEffect(() => {
    if (!sessionId) {
      setMessages([
        {
          id: generateUniqueId('welcome'),
          type: 'system',
          content: 'Welcome to Deceptive Design Agent. Please create a new session to begin.'
        }
      ]);
    } else {
      loadSessionData();
    }
  }, [sessionId, loadSessionData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load domains from prop or localStorage
  useEffect(() => {
    if (userDomains && userDomains.length > 0) {
      console.log('User domains updated:', userDomains);
      setLocalUserDomains(userDomains);
    } else {
      // Try to load from localStorage as fallback if prop is empty
      const storedDomains = localStorage.getItem('userDomains');
      if (storedDomains) {
        try {
          const parsedDomains = JSON.parse(storedDomains);
          if (Array.isArray(parsedDomains) && parsedDomains.length > 0) {
            console.log('Loading domains from localStorage:', parsedDomains);
            setLocalUserDomains(parsedDomains);
          }
        } catch (error) {
          console.error('Error parsing stored domains:', error);
        }
      }
    }
  }, [userDomains]);

  useEffect(() => {
    if (sessionId && selectedJourney) {
      loadJourneyImages();
    }
  }, [sessionId, selectedJourney, loadJourneyImages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.selected-journey-display')) {
        setShowJourneyDropdown(false);
      }
      if (!target.closest('.create-journey-form')) {
        setShowPredefinedDropdown(false);
      }
      if (!target.closest('.journey-selection-dropdown')) {
        setShowUserJourneyDropdown(false);
      }
    };

    if (showJourneyDropdown || showPredefinedDropdown || showUserJourneyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showJourneyDropdown, showPredefinedDropdown, showUserJourneyDropdown]);

  const handleAddNewDomain = async () => {
    try {
      if (!newDomainInput.trim()) {
        alert('Please enter a domain name');
        return;
      }

      setIsProcessing(true);
      const updatedDomains = await addDomainToUser(userEmail, newDomainInput.trim());

      // Update local state
      setLocalUserDomains(updatedDomains);

      // Update localStorage
      localStorage.setItem('userDomains', JSON.stringify(updatedDomains));

      // Select the newly added domain
      setDomainName(newDomainInput.trim());

      // Reset form
      setNewDomainInput('');
      setIsAddingNewDomain(false);

      alert(`Domain "${newDomainInput.trim()}" added successfully!`);
    } catch (error) {
      console.error('Error adding domain:', error);
      alert('Failed to add domain');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateSession = async () => {
    try {
      if (!sessionName.trim()) {
        alert('Please enter a session name');
        return;
      }

      if (!domainName.trim()) {
        alert('Please enter a domain name');
        return;
      }

      if (!userSession) {
        alert('User session not available. Please log in again.');
        return;
      }

      setIsProcessing(true);
      // Create session with user email, domain name, admin ID, and user role
      const newSessionId = await createSession(
        sessionName,
        userEmail,
        domainName,
        userSession.adminId,
        userSession.tag
      );

      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('session-created'),
          type: 'system',
          content: `Session "${sessionName}" created for domain "${domainName}". Now create a user journey.`
        }
      ]);

      // Redirect to the new session
      window.location.href = `/session/${newSessionId}`;
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateJourney = async () => {
    try {
      if (!sessionId) {
        alert('Please create a session first');
        return;
      }

      if (!journeyName.trim()) {
        alert('Please enter a journey name');
        return;
      }

      setIsProcessing(true);
      const newJourneyId = await createUserJourney(sessionId, journeyName, 'AI Agent');

      const newJourney = {
        id: newJourneyId,
        journeyName,
        sessionId,
        createdAt: new Date(),
        createdBy: 'AI Agent'
      };

      setJourneys(prev => [...prev, newJourney]);
      setSelectedJourney(newJourneyId);
      setIsCreatingJourney(false);
      setJourneyName(''); // Reset journey name

      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('journey-created'),
          type: 'system',
          content: `User journey "${journeyName}" created. You can now upload an image or provide a public URL.`
        }
      ]);
    } catch (error) {
      console.error('Error creating journey:', error);
      alert('Failed to create user journey');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
      setImageUrl('');
      setScreenCapture(null);
    }
  };

  /* Removed unused addImageToQueue and removeImageFromQueue */

  const handleCaptureScreen = async () => {
    try {
      setIsCapturing(true);

      // @ts-ignore - TypeScript doesn't fully recognize all getDisplayMedia properties
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          // Request high resolution capture for complete screen
          width: { ideal: 1920, max: 3840 },
          height: { ideal: 1080, max: 2160 }
        },
        audio: false
      });

      // Create a video element to capture the stream
      const video = document.createElement('video');
      video.srcObject = mediaStream;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;

      // Wait for video to be ready and playing
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => {
          video.play().then(() => resolve());
        };
      });

      // Wait longer for the video to stabilize and show full content
      await new Promise(resolve => setTimeout(resolve, 500));

      // Take a screenshot
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      console.log('Capturing screen with dimensions:', {
        width: video.videoWidth,
        height: video.videoHeight
      });

      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Draw the complete video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to data URL with high quality
        const dataUrl = canvas.toDataURL('image/png', 1.0);
        setScreenCapture(dataUrl);
        setImageFile(null);
        setImageUrl('');

        console.log('Screen captured successfully:', {
          dataUrlLength: dataUrl.length,
          sizeKB: Math.round(dataUrl.length / 1024)
        });
      }

      // Stop all tracks immediately after capture
      mediaStream.getTracks().forEach(track => track.stop());
      setIsCapturing(false);
    } catch (error) {
      console.error('Error capturing screen:', error);
      alert('Failed to capture screen. Please make sure you grant the necessary permissions.');
      setIsCapturing(false);
    }
  };


  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };


  const handleViewImage = async (imageId: string) => {
    try {
      if (!sessionId || !selectedJourney) return;

      let image = journeyImages.find(img => img.id === imageId) || null;
      if (!image) {
        image = await getImage(sessionId, selectedJourney, imageId);
      }
      if (image) {
        const selectedImage = image;
        setCurrentImage(selectedImage);

        // Log the image data for debugging
        console.log('Viewing image data:', selectedImage);

        // Switch to conversation tab to show results
        setActiveTab('conversation');

        // Start with a fresh message list
        setMessages([]);

        // Add image to messages
        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('view-image'),
            type: 'image' as const,
            content: 'Previously analyzed image:',
            imageUrl: selectedImage.imageUrl
          }
        ]);

        // Add chat history if available
        if (selectedImage.chatHistory && selectedImage.chatHistory.length > 0) {
          console.log('Found chat history:', selectedImage.chatHistory);

          // Add a separator
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('chat-history-header'),
              type: 'system' as const,
              content: 'Previous conversation with this image:'
            }
          ]);

          // Add each Q&A pair from the history
          selectedImage.chatHistory.forEach((entry, index) => {
            // Add the question
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId(`history-question-${index}`),
                type: 'user' as const,
                content: entry.question
              }
            ]);

            // Add the answer with highlighting
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId(`history-answer-${index}`),
                type: 'system' as const,
                content: `<div class="chat-response-highlight">${entry.answer}</div>`
              }
            ]);
          });

          // Add a separator
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('response-header'),
              type: 'system' as const,
              content: 'Compliance issue:'
            }
          ]);
        }

        // Add response to messages if available
        if (selectedImage.response) {
          console.log('Response structure:', selectedImage.response);

          // Process response data - filter for type: "outcome" only
          let points: PointData[] = [];
          try {
            // First check if response is already an array
            if (Array.isArray(selectedImage.response)) {
              // Filter only items with type: "outcome"
              points = selectedImage.response
                .filter(item => item.type === 'outcome' || item.kind === 'outcome')
                .map(item => ({
                  text: item.text || 'No text provided',
                  kind: item.dark_pattern || item.kind || item.type || 'Unknown',
                  type: item.type || 'outcome',
                  laws: item.laws || []
                }));
            } else {
              // Otherwise, treat it as an object with keys
              points = Object.values(selectedImage.response).flat()
                .filter((item: any) => item.type === 'outcome' || item.kind === 'outcome')
                .map((item: any) => {
                  console.log('Response item:', item);
                  return {
                    text: item.text || 'No text provided',
                    kind: item.dark_pattern || item.kind || item.type || 'Unknown',
                    type: item.type || 'outcome',
                    laws: item.laws || []
                  };
                });
            }
          } catch (err) {
            console.error('Error processing response:', err);
            points = [{
              text: 'Error processing response data. See console for details.',
              kind: 'Error',
              type: 'error',
              laws: []
            }];
          }

          if (points.length > 0) {
            const journey = journeys.find(j => j.id === selectedJourney);
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId('view-response'),
                type: 'response' as const,
                content: 'Compliance issue:',
                points,
                journeyName: journey?.journeyName,
                className: selectedImage.feedback ? 'refined-response-highlight' : ''
              }
            ]);
          } else {
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId('empty-response'),
                type: 'system' as const,
                content: 'No specific patterns were identified in this image.'
              }
            ]);
          }

          // If there's feedback, show it
          if (selectedImage.feedback) {
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId('feedback-info'),
                type: 'system' as const,
                content: `<div class="refined-response-highlight"><strong>This analysis was refined based on feedback:</strong> "${selectedImage.feedback}"</div>`
              }
            ]);
          }
        } else {
          // If no response, show a message
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('no-response'),
              type: 'system' as const,
              content: 'No analysis results available for this image.'
            }
          ]);
        }

        // Tab is already switched at the beginning of the function
      }
    } catch (error) {
      console.error('Error viewing image:', error);
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system' as const,
          content: 'Failed to load image details. ' + (error instanceof Error ? error.message : '')
        }
      ]);
    }
  };

  // Handle conversation question and answer flow
  const handleConversation = async (imageId: string, imageUrl: string, metadata: any) => {
    try {
      // Get the image document to access its context
      if (!sessionId || !selectedJourney) {
        throw new Error('Session or journey not selected');
      }

      const imageDoc = await getImage(sessionId, selectedJourney, imageId);
      if (!imageDoc) {
        throw new Error('Image not found');
      }

      // Use the context directly from the image document if available
      let contextText = imageDoc.context || '';

      // If no context in the image document, try to extract from metadata as fallback
      if (!contextText && metadata) {
        if (metadata.context) {
          contextText = metadata.context;
        } else if (metadata.Scanner_with_context) {
          contextText = metadata.Scanner_with_context;
        } else if (metadata.prompt_context) {
          contextText = metadata.prompt_context;
        } else if (metadata.webpage_brief) {
          contextText = typeof metadata.webpage_brief === 'string'
            ? metadata.webpage_brief
            : JSON.stringify(metadata.webpage_brief);
        } else {
          // Combine all available text fields as fallback
          const textFields = [
            'scanner', 'call_to_action', 'nature_of_design', 'information_organization',
            'typography_and_colour_use', 'footnote_or_information', 'pageName'
          ];

          contextText = textFields
            .filter(field => metadata[field])
            .map(field => metadata[field])
            .join(' ');
        }
      }

      if (!contextText) {
        contextText = "Please analyze this image for deceptive design patterns.";
      }

      console.log('Using context for questions:', contextText);

      // Get questions based on context
      const questions = await getContextBasedQuestions(contextText);

      if (!questions || questions.length === 0) {
        throw new Error('No questions returned from the API');
      }

      // First reset any existing conversation state
      setCurrentQuestionIndex(0);
      setAnswers({});

      // Then set the new questions
      setCurrentQuestions(questions);

      // Update current image for reference in answer submission
      setCurrentImage(imageDoc);

      // Add a system message with context info
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('context'),
          type: 'system' as const,
          content: 'Based on the initial analysis, I will ask you some questions to gather more context.'
        }
      ]);

      // Wait a moment to ensure state updates have processed
      await new Promise(resolve => setTimeout(resolve, 50));

      // Ask the first question
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('question'),
          type: 'question' as const,
          content: questions[0]
        }
      ]);

    } catch (error) {
      console.error('Error starting conversation:', error);

      // Display error message to user
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system' as const,
          content: 'Failed to start conversation. Please try again or switch to direct analysis mode.'
        }
      ]);

      // Don't fall back to direct processing in conversation mode
      // This was causing the wrong endpoint to be called
    }
  };

  const handleAnswerSubmit = async (sessionId: string, journeyId: string, imageId: string, imageUrl: string) => {
    try {
      if (isProcessing) {
        return; // Prevent multiple submissions
      }

      setIsProcessing(true);

      // Add the current answer to the answers object
      const updatedAnswers = {
        ...answers,
        [currentQuestions[currentQuestionIndex]]: currentAnswer || 'None'
      };
      setAnswers(updatedAnswers);

      // Add the answer to the messages
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('user'),
          type: 'user' as const,
          content: currentAnswer || 'None'
        }
      ]);

      // Clear the current answer input
      setCurrentAnswer('');

      // Wait a moment for state updates
      await new Promise(resolve => setTimeout(resolve, 50));

      // Check if we have more questions
      if (currentQuestionIndex < currentQuestions.length - 1) {
        // Move to the next question
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);

        // Wait a moment for state update
        await new Promise(resolve => setTimeout(resolve, 50));

        // Add the next question to the messages
        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('question'),
            type: 'question' as const,
            content: currentQuestions[nextIndex]
          }
        ]);

        setIsProcessing(false);
      } else {
        // All questions answered, prepare the conversation object
        const conversation: Conversation = {
          questions: currentQuestions,
          answers: updatedAnswers
        };

        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('system'),
            type: 'system' as const,
            content: 'Thank you for answering the questions. Processing your image with the provided context...'
          }
        ]);

        // First, save the conversation to update the context in the image document
        await saveConversation(sessionId, journeyId, imageId, conversation);

        // Now call completeImageProcessing which will use the updated context
        await completeImageProcessing(
          sessionId,
          journeyId,
          imageId,
          imageUrl,
          targetUrl || '',
          note
        );

        // Get the processed image data
        const processedImage = await getImage(sessionId, journeyId, imageId);
        setCurrentImage(processedImage);

        // Automatically switch to conversation tab to show results
        setActiveTab('conversation');

        // Display results
        console.log('Processed image data:', processedImage);

        if (processedImage && processedImage.response) {
          console.log('Response structure:', processedImage.response);

          // Process response data
          let points: PointData[] = [];
          try {
            points = Object.values(processedImage.response).flat().map(item => {
              console.log('Response item:', item);
              return {
                text: item.text || 'No text provided',
                kind: item.kind || item.type || 'Unknown'
              };
            });

            console.log('Extracted points:', points);
          } catch (err) {
            console.error('Error processing response points:', err);
            points = [{
              text: 'Error processing response data. See console for details.',
              kind: 'Error'
            }];
          }

          // Add response to chat
          const journey = journeys.find(j => j.id === selectedJourney);
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('response-' + imageId),
              type: 'response' as const,
              content: 'Analysis complete. Here are the deceptive design patterns found:',
              points,
              journeyName: journey?.journeyName
            }
          ]);

          // Ask the user about further actions
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('next-steps'),
              type: 'system' as const,
              content: 'Would you like to analyze another image or create a new user journey?'
            }
          ]);
        } else {
          console.error('No response found in processed image:', processedImage);

          // Show error if no response
          setMessages(prev => [
            ...prev,
            {
              id: generateUniqueId('no-response'),
              type: 'system' as const,
              content: 'No analysis results were returned. The image may not contain recognizable deceptive design patterns or there was an error in processing.'
            }
          ]);
        }

        // Reset conversation state
        setCurrentQuestions([]);
        setCurrentQuestionIndex(0);
        setAnswers({});
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error processing answer:', error);
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system' as const,
          content: 'Failed to process your answer. Please try again.'
        }
      ]);
      setIsProcessing(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!sessionId || !selectedJourney) {
        alert('Please select a user journey');
        return;
      }

      const imageSources = [
        ...imageFiles.map(file => ({ file, url: null })),
        ...imageUrls.map(url => ({ file: null, url })),
      ];

      if (imageSources.length === 0) {
        if (imageFile) {
          imageSources.push({ file: imageFile, url: null });
        } else if (screenCapture) {
          const file = dataURLtoFile(screenCapture, 'screen-capture.png');
          imageSources.push({ file, url: null });
        } else if (imageUrl) {
          imageSources.push({ file: null, url: imageUrl });
        } else {
          alert('Please provide at least one image');
          return;
        }
      }

      setIsProcessing(true);

      // Add user message to chat
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('user'),
          type: 'user',
          content: note || 'Please analyze these images for deceptive design patterns.'
        }
      ]);

      // Process all images in parallel but with separate endpoint calls
      // All images will use the same note, targetUrl, etc.
      const processingPromises = imageSources.map(async ({ file, url }) => {
        // Upload the image with common note and URL for all images
        const imageId = await uploadImage(
          sessionId,
          selectedJourney,
          file,
          url,
          targetUrl || '',
          note
        );

        // Show the image in chat
        const displayImageUrl = file
          ? URL.createObjectURL(file)
          : url || '';

        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('image-' + imageId),
            type: 'image',
            content: 'Image uploaded successfully. Processing...',
            imageUrl: displayImageUrl
          }
        ]);

        // Process the image based on the mode (conversation or direct)
        if (useConversationMode) {
          try {
            // Process with step 1 only
            const step1Result = await processImageStep1(
              sessionId,
              selectedJourney,
              imageId,
              displayImageUrl,
              targetUrl || '',  // Common URL for all images
              note  // Common note for all images
            );

            // Start conversation flow
            await handleConversation(imageId, displayImageUrl, step1Result.data?.step1_metadata);
          } catch (error) {
            console.error('Error in conversation mode:', error);

            // If conversation mode fails, notify the user but don't fall back to direct processing
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId('error'),
                type: 'system',
                content: 'Conversation mode failed. Please try again or switch to direct analysis mode.'
              }
            ]);
          }
        } else {
          // Choose processing method based on fetchLaws toggle
          if (fetchLaws) {
            // Full analysis with laws
            await processImage(
              sessionId,
              selectedJourney,
              imageId,
              displayImageUrl,
              targetUrl || '',  // Common URL for all images
              note  // Common note for all images
            );
          } else {
            // Quick analysis without laws
            await processImageQuickAnalysis(
              sessionId,
              selectedJourney,
              imageId,
              displayImageUrl,
              targetUrl || '',  // Common URL for all images
              note  // Common note for all images
            );
          }

          // Get the processed image data
          const processedImage = await getImage(sessionId, selectedJourney, imageId);

          if (processedImage && processedImage.response) {
            const points: PointData[] = Object.values(processedImage.response).flat().map(item => ({
              text: item.text,
              kind: item.kind || item.type || 'Unknown',
              type: item.type,
              laws: item.laws || []
            }));

            // Add response to chat
            const journey = journeys.find(j => j.id === selectedJourney);
            setMessages(prev => [
              ...prev,
              {
                id: generateUniqueId('response-' + imageId),
                type: 'response',
                content: fetchLaws
                  ? 'Analysis complete. Here are the deceptive design patterns found:'
                  : 'Quick analysis complete. Here are the UI/UX insights:',
                points,
                journeyName: journey?.journeyName
              }
            ]);

            // Automatically switch to conversation tab to show results
            setActiveTab('conversation');

            // Set current image for interaction buttons
            setCurrentImage(processedImage);
          }

          return processedImage;
        }
      });

      // Wait for all processing to complete
      await Promise.all(processingPromises);

      // Refresh journey images
      loadJourneyImages();

      // Reset form
      setImageFile(null);
      setImageUrl('');
      setScreenCapture(null);
      setTargetUrl('');
      setNote('');
      setImageFiles([]);
      setImageUrls([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system',
          content: 'Failed to process image(s). Please try again.'
        }
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Add this CSS to add highlighting for chat and refinement responses
  const styles = `
    .chat-response-highlight {
      background-color: rgba(13, 110, 253, 0.1);
      border-left: 4px solid #0d6efd;
      padding: 12px;
      border-radius: 4px;
      margin: 8px 0;
      white-space: pre-line;
    }
    
    .refined-response-highlight {
      background-color: rgba(25, 135, 84, 0.1);
      border-left: 4px solid #198754;
      padding: 12px;
      border-radius: 4px;
      margin: 8px 0;
      white-space: pre-line;
    }
    
    .message.response.refined-response-highlight,
    .message.response .refined-response-highlight {
      border-left-color: #198754;
      background-color: rgba(25, 135, 84, 0.05);
    }
    
    ul.list-group.refined-response-highlight {
      border-left: 4px solid #198754;
      background-color: rgba(25, 135, 84, 0.05);
      padding: 8px;
    }
    
    .response-type-indicator {
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(13, 110, 253, 0.2);
    }
    
    .response-content {
      margin-top: 4px;
    }
    
    .response-content strong {
      font-weight: 600;
    }
  `;

  // Update the component to include the styles
  useEffect(() => {
    // Add the styles to the document
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [styles]);

  // Update the renderMessageContent function to handle HTML content
  const renderMessageContent = (message: any) => {
    switch (message.type) {
      case 'image':
        return (
          <>
            <p>{message.content}</p>
            {message.imageUrl && (
              <div className="image-container">
                {message.imageUrl.startsWith('data:image') && message.imageUrl.length > 100000 ? (
                  <div className="base64-image-placeholder">
                    <p className="text-muted">Large base64 image (size: {Math.round(message.imageUrl.length / 1024)} KB)</p>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => window.open(message.imageUrl, '_blank')}
                    >
                      <i className="bi bi-eye me-1"></i> View Full Image
                    </Button>
                  </div>
                ) : (
                  <Image src={message.imageUrl} fluid style={{ maxHeight: '300px' }} />
                )}
              </div>
            )}
          </>
        );
      case 'response':
        const complianceCount = message.points ? message.points.length : 0;

        return (
          <>
            {/* Agent Header */}
            <div className="compliance-response-header">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-robot" style={{ fontSize: '20px', color: '#7752FF' }}></i>
                <strong style={{ color: '#475467', fontSize: '14px' }}>Deceptive Design Agent</strong>
              </div>
              <p style={{ color: '#475467', fontSize: '14px', marginBottom: '4px' }}>
                {message.journeyName || 'Image'} analysed.
              </p>
              <p style={{ color: '#475467', fontSize: '14px', fontWeight: 600 }}>
                {complianceCount} compliance issue{complianceCount !== 1 ? 's' : ''} found.
              </p>
            </div>

            {/* Analysis Results Label */}
            <p style={{ fontWeight: 700, color: '#475467', fontSize: '14px', marginTop: '16px', marginBottom: '12px' }}>
              Compliance issue:
            </p>

            {/* Compliance Issues */}
            {message.points && message.points.length > 0 ? (
              <div className="compliance-issues-list">
                {message.points.map((point: any, i: number) => (
                  <ComplianceIssueCard
                    key={i}
                    point={point}
                    index={i}
                    messageId={message.id}
                    expandedLaws={expandedLaws}
                    toggleLawExpansion={toggleLawExpansion}
                  />
                ))}
              </div>
            ) : (
              <div className="alert alert-info">
                No specific patterns were identified. The analysis may not have found any deceptive design patterns in this image.
              </div>
            )}

          </>
        );
      case 'system':
        // Check if content is HTML by looking for HTML tags
        if (message.content.includes('<div class=')) {
          // Format content to properly display bold text (**text**)
          let formattedContent = message.content;
          // Replace Markdown-style bold with HTML strong tags
          if (!message.content.includes('<strong>') && message.content.includes('**')) {
            formattedContent = message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          }
          return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
        }
        // Handle plain text with Markdown-style bold
        if (message.content.includes('**')) {
          const formattedContent = message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
        }
        return message.content;
      case 'question':
        // Only show answer form for the current active question
        const isCurrentQuestion = message.content === currentQuestions[currentQuestionIndex];

        return (
          <>
            <p className="fw-bold">{message.content}</p>
            {isCurrentQuestion && (
              <Form onSubmit={(e) => {
                e.preventDefault();
                if (!sessionId || !selectedJourney || !currentImage) return;
                handleAnswerSubmit(sessionId, selectedJourney, currentImage.id, currentImage.imageUrl);
              }}>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Type your answer here..."
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    disabled={isProcessing}
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isProcessing}
                  size="sm"
                >
                  {isProcessing ? (
                    <><Spinner animation="border" size="sm" /> Processing...</>
                  ) : 'Submit Answer'}
                </Button>
              </Form>
            )}
          </>
        );
      default:
        // Check for Markdown-style bold in other message types
        if (message.content.includes('**')) {
          const formattedContent = message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
        }
        return message.content;
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  // Handle chat with image
  const handleChatWithImage = async () => {
    try {
      if (!sessionId || !selectedJourney || !currentImage || !chatQuestion.trim()) {
        alert('Please select an image and enter a question');
        return;
      }

      setIsProcessing(true);

      // Add user question to messages
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('chat-question'),
          type: 'user' as const,
          content: chatQuestion
        }
      ]);

      // Call the API
      const result = await chatWithImage(
        sessionId,
        selectedJourney,
        currentImage.id,
        chatQuestion
      );

      // Get the formatted response text from the result
      let responseText = result.formattedResponse ||
        (result.data && result.data.response) ||
        result.response ||
        'No response received';

      // Log the response for debugging
      console.log('Chat response received:', responseText);

      // Format Markdown-style bold text to HTML
      if (responseText.includes('**')) {
        responseText = responseText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      }

      // Add response type indicator for clarity
      const formattedResponse = `
        <div class="chat-response-highlight">
          <div class="response-type-indicator">
            <small class="text-primary fw-bold"><i class="bi bi-robot"></i> Deceptive Design Agent:</small>
          </div>
          <div class="response-content">
            ${responseText}
          </div>
        </div>
      `;

      // Add response to messages with enhanced highlighting styling
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('chat-response'),
          type: 'system' as const,
          content: formattedResponse
        }
      ]);

      // Get the updated image to get latest chat history
      const updatedImage = await getImage(sessionId, selectedJourney, currentImage.id);
      setCurrentImage(updatedImage);

      // Reset question
      setChatQuestion('');
    } catch (error) {
      console.error('Error chatting with image:', error);
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system' as const,
          content: 'Failed to chat with image: ' + (error instanceof Error ? error.message : 'Unknown error')
        }
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle refine outcome
  const handleRefineOutcome = async () => {
    try {
      if (!sessionId || !selectedJourney || !currentImage || !refineFeedback.trim()) {
        alert('Please select an image and enter feedback');
        return;
      }

      setIsProcessing(true);

      // Add user feedback to messages
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('refine-feedback'),
          type: 'user' as const,
          content: `Feedback: ${refineFeedback}`
        }
      ]);

      // Add processing message
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('refine-processing'),
          type: 'system' as const,
          content: 'Processing your feedback and generating a refined analysis...'
        }
      ]);

      // Call the API
      try {
        await postUserChatFeedback(currentImage.id, refineFeedback);
      } catch (feedbackError) {
        console.error('Error sending chat feedback:', feedbackError);
      }

      const result = await regenerateWithFeedback(
        sessionId,
        selectedJourney,
        currentImage.id,
        refineFeedback
      );

      console.log('Refined result:', result);

      // Get the updated image with the new response
      const updatedImage = await getImage(sessionId, selectedJourney, currentImage.id);
      setCurrentImage(updatedImage);

      // Process and display the refined outcome
      if (updatedImage && updatedImage.response) {
        console.log('Refined response:', updatedImage.response);

        // Extract points from the response
        let points: PointData[] = [];
        try {
          if (Array.isArray(updatedImage.response)) {
            points = updatedImage.response.map(item => ({
              text: item.text || 'No text provided',
              kind: item.kind || item.type || 'Unknown'
            }));
          } else {
            points = Object.values(updatedImage.response).flat().map(item => ({
              text: item.text || 'No text provided',
              kind: item.kind || item.type || 'Unknown'
            }));
          }
        } catch (err) {
          console.error('Error processing response points:', err);
          points = [{
            text: 'Error processing response data. See console for details.',
            kind: 'Error'
          }];
        }

        // Add refined response to messages with highlight class
        const journey = journeys.find(j => j.id === selectedJourney);
        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('refined-response'),
            type: 'response' as const,
            content: 'Refined analysis based on your feedback:',
            points,
            journeyName: journey?.journeyName,
            className: 'refined-response-highlight' // Add a class for styling
          }
        ]);
      } else if (result.formattedResponse) {
        // If there's a formatted response but no points, display as text
        let formattedResponse = result.formattedResponse;

        // Format Markdown-style bold text to HTML
        if (formattedResponse.includes('**')) {
          formattedResponse = formattedResponse.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        }

        // Add response type indicator for clarity
        const enhancedResponse = `
          <div class="refined-response-highlight">
            <div class="response-type-indicator">
              <small class="text-success fw-bold"><i class="bi bi-check-circle"></i> REFINED ANALYSIS:</small>
            </div>
            <div class="response-content">
              ${formattedResponse}
            </div>
          </div>
        `;

        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('refined-text-response'),
            type: 'system' as const,
            content: enhancedResponse
          }
        ]);
      } else {
        setMessages(prev => [
          ...prev,
          {
            id: generateUniqueId('no-refined-response'),
            type: 'system' as const,
            content: 'Could not retrieve the refined analysis. Please try again.'
          }
        ]);
      }

      // Reset feedback and switch to conversation tab to show results
      setRefineFeedback('');
      setActiveTab('conversation');
    } catch (error) {
      console.error('Error refining outcome:', error);
      setMessages(prev => [
        ...prev,
        {
          id: generateUniqueId('error'),
          type: 'system' as const,
          content: 'Failed to refine outcome: ' + (error instanceof Error ? error.message : 'Unknown error')
        }
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!sessionId) {
    // Welcome / Session creation screen
    return (
      <div className="welcome-container">

        {/* Welcome Header */}
        <div className="welcome-header">
          <h1 className="welcome-title">Welcome to Fair Design Agent</h1>
          <div className="welcome-button-group">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '14px',
              color: 'var(--text-secondary)'
            }}>
              <span style={{ fontWeight: 500 }}>
                <i className="bi bi-person-circle me-2"></i>
                {userEmail}
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={onLogout}
                style={{
                  fontWeight: 600,
                  borderRadius: '8px',
                  padding: '0.375rem 1rem'
                }}
              >
                <i className="bi bi-box-arrow-right me-1"></i>
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="info-card">
          <div className="info-card-content">
            <div className="info-card-title">Spot deceptive designs before regulators do</div>
            <div className="info-card-text">
              <p style={{ marginBottom: '10px' }}>
                Fair Design Agent scans your web mockups for dark patterns and other digital violations. Just ask questions in plain language and get instant answers about what might be problematic.
              </p>
              <p style={{ fontWeight: 700, marginBottom: '0' }}>How to do it:</p>
              <ul>
                <li style={{ marginBottom: '0' }}>Create a new analysis session by entering a domain name and a session name below.</li>
                <li>Upload screenshots or provide URLs to analyze for dark patterns and compliance issues.</li>
              </ul>
              <div style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="bi bi-check-circle-fill" style={{
                  fontSize: '14px',
                  color: '#198754'
                }}></i>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#000EAB'
                }}>
                  Make digital fairness part of your launch checklist
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Create Session Card */}
        <div className="create-session-card">
          <div className="create-session-header">
            <i className="bi bi-lightning-charge-fill create-session-icon"></i>
            <div className="create-session-title">Please create a new session to begin</div>
          </div>

          {/* Session Group (Domain) */}
          <div className="form-group-custom">
            <label className="form-label-custom" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Domain name</span>
              <i
                className="bi bi-info-circle"
                style={{ fontSize: '14px', color: 'var(--text-tertiary)', cursor: 'help' }}
                title="Domains help the system identify which reports and sessions belong together."
              ></i>
            </label>
            {!isAddingNewDomain ? (
              <>
                <select
                  className="form-control-custom"
                  value={domainName}
                  onChange={(e) => {
                    if (e.target.value === '__add_new__') {
                      setIsAddingNewDomain(true);
                      setDomainName('');
                    } else {
                      setDomainName(e.target.value);
                    }
                  }}
                  disabled={isProcessing}
                  style={{
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23667085' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 16px center',
                    paddingRight: '40px'
                  }}
                >
                  <option value="">Select or enter the domain for which you want to create a session, for example hotels.com</option>
                  {localUserDomains && localUserDomains.length > 0 && (
                    localUserDomains.map((domain, index) => (
                      <option key={index} value={domain}>
                        {domain}
                      </option>
                    ))
                  )}
                  <option value="__add_new__">➕ Add New Domain</option>
                </select>
              </>
            ) : (
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control-custom"
                  placeholder="Enter new domain (e.g., www.example.com)"
                  value={newDomainInput}
                  onChange={(e) => setNewDomainInput(e.target.value)}
                  disabled={isProcessing}
                />
                <Button
                  variant="success"
                  onClick={handleAddNewDomain}
                  disabled={isProcessing || !newDomainInput.trim()}
                  style={{ minWidth: '80px' }}
                >
                  {isProcessing ? <Spinner animation="border" size="sm" /> : 'Add'}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setIsAddingNewDomain(false);
                    setNewDomainInput('');
                  }}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Session Name */}
          <div className="form-group-custom">
            <label className="form-label-custom">Session name</label>
            <input
              type="text"
              className="form-control-custom"
              placeholder="Name your session, for example Website Revamping"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Create Session Button */}
          <button
            className="btn-create-session"
            onClick={handleCreateSession}
            disabled={isProcessing || !sessionName.trim() || !domainName.trim()}
          >
            {isProcessing ? (
              <><Spinner animation="border" size="sm" /> Creating Session...</>
            ) : 'Create Session'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="session-container">

      {isCapturing && (
        <div className="screen-capture-overlay">
          <div className="capture-controls">
            <Spinner animation="border" variant="light" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-white">Capturing screen...</p>
            <p className="text-white-50 small">Please select what you want to capture in the system dialog</p>
          </div>
        </div>
      )}

      {/* Session Header */}
      <div className="session-header">
        <div className="session-info">
          <div className="session-label">Session Name</div>
          <h1 className="session-name">{sessionName || 'Untitled Session'}</h1>
        </div>
        <div className="session-actions">
          <span className="user-info">
            <i className="bi bi-person-circle me-2"></i>
            {userEmail}
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onLogout}
            className="logout-btn"
          >
            <i className="bi bi-box-arrow-right me-1"></i>
            Logout
          </Button>
        </div>
      </div>

      {/* User Journey Section - Always Visible, comes right after session header */}
      <div className="user-journey-section">
        {isCreatingJourney ? (
          <div className="create-journey-form" style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            padding: '24px 32px 32px 32px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            {/* Predefined Journeys Dropdown */}
            <div style={{ position: 'relative', marginBottom: '10px' }}>
              <div
                onClick={() => setShowPredefinedDropdown(!showPredefinedDropdown)}
                style={{
                  backgroundColor: '#fcfcfd',
                  border: '1px solid #7752ff',
                  borderRadius: '5px',
                  height: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  cursor: 'pointer'
                }}
              >
                <span style={{
                  fontSize: '14px',
                  color: PREDEFINED_JOURNEYS.includes(journeyName) ? '#101828' : '#9ca3af',
                  fontFamily: 'Circular Std, sans-serif',
                  fontWeight: 400
                }}>
                  {PREDEFINED_JOURNEYS.includes(journeyName) ? journeyName : 'Select your user journey'}
                </span>
                <i className={`bi bi-chevron-${showPredefinedDropdown ? 'up' : 'down'}`} style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}></i>
              </div>

              {/* Dropdown Menu */}
              {showPredefinedDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  zIndex: 1000,
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {PREDEFINED_JOURNEYS.map(journey => (
                    <button
                      key={journey}
                      onClick={() => {
                        setJourneyName(journey);
                        setShowPredefinedDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: journeyName === journey ? '#f0f9ff' : 'none',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: journeyName === journey ? '#1e40af' : '#374151',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        fontWeight: journeyName === journey ? 600 : 400
                      }}
                      onMouseEnter={(e) => {
                        if (journeyName !== journey) {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (journeyName !== journey) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {journey}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Name Input */}
            <div style={{
              backgroundColor: '#fcfcfd',
              border: !PREDEFINED_JOURNEYS.includes(journeyName) && journeyName ? '1px solid #7752ff' : '1px solid #000eab',
              borderRadius: '5px',
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              marginBottom: '24px',
              transition: 'border-color 0.2s ease'
            }}>
              <input
                type="text"
                placeholder="Enter a name for your user journey"
                value={PREDEFINED_JOURNEYS.includes(journeyName) ? '' : journeyName}
                onChange={(e) => {
                  const customName = e.target.value;
                  setJourneyName(customName);
                }}
                disabled={isProcessing}
                onFocus={(e) => {
                  e.currentTarget.parentElement!.style.borderColor = '#7752ff';
                }}
                onBlur={(e) => {
                  e.currentTarget.parentElement!.style.borderColor = !PREDEFINED_JOURNEYS.includes(journeyName) && journeyName ? '#7752ff' : '#000eab';
                }}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                  color: '#101828',
                  fontFamily: 'Circular Std, sans-serif',
                  fontWeight: 400,
                  outline: 'none',
                  lineHeight: '21px'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <button
                onClick={handleCreateJourney}
                disabled={isProcessing || !journeyName.trim()}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#7752ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: 'Circular Std, sans-serif',
                  cursor: isProcessing || !journeyName.trim() ? 'not-allowed' : 'pointer',
                  opacity: isProcessing || !journeyName.trim() ? 0.5 : 1,
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isProcessing && journeyName.trim()) {
                    e.currentTarget.style.backgroundColor = '#6b46c1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProcessing && journeyName.trim()) {
                    e.currentTarget.style.backgroundColor = '#7752ff';
                  }
                }}
              >
                {isProcessing ? (
                  <><Spinner animation="border" size="sm" /> Creating...</>
                ) : 'Select Journey'}
              </button>
              <button
                onClick={() => {
                  setIsCreatingJourney(false);
                  setJourneyName('');
                }}
                disabled={isProcessing}
                style={{
                  padding: '12px 20px',
                  backgroundColor: 'white',
                  color: '#101828',
                  border: '1px solid #d0d5dd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: 'Circular Std, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : selectedJourney ? (
          <div className="selected-journey-display" style={{ position: 'relative' }}>
            <div className="journey-header-with-dropdown" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease'
            }}
              onClick={() => setShowJourneyDropdown(!showJourneyDropdown)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#7752ff',
                borderRadius: '6px',
                color: 'white'
              }}>
                <i className="bi bi-lightning-charge-fill" style={{ fontSize: '16px' }}></i>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1
              }}>
                <span style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#101828',
                  fontFamily: 'Circular Std, sans-serif'
                }}>
                  User Journey - {journeys.find(j => j.id === selectedJourney)?.journeyName || 'Unknown'}
                </span>
                <i className={`bi bi-chevron-${showJourneyDropdown ? 'up' : 'down'}`} style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}></i>
              </div>
            </div>

            {/* Dropdown Menu */}
            {showJourneyDropdown && (
              <div className="journey-dropdown-menu" style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                zIndex: 1000,
                marginTop: '4px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {/* User's Custom Journeys Section */}
                {journeys && journeys.length > 0 && (
                  <div style={{
                    padding: '8px 0',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Your Journeys
                    </div>
                    {journeys.map(journey => (
                      <button
                        key={journey.id}
                        onClick={() => {
                          setSelectedJourney(journey.id);
                          setShowJourneyDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: selectedJourney === journey.id ? '#f0f9ff' : 'none',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: selectedJourney === journey.id ? '#1e40af' : '#374151',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          fontWeight: selectedJourney === journey.id ? 600 : 400
                        }}
                        onMouseEnter={(e) => {
                          if (selectedJourney !== journey.id) {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedJourney !== journey.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {journey.journeyName}
                      </button>
                    ))}
                  </div>
                )}

                {/* Create New Journey */}
                <div style={{
                  padding: '8px 0'
                }}>
                  <button
                    onClick={() => {
                      setIsCreatingJourney(true);
                      setSelectedJourney(null);
                      setShowJourneyDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#7752ff',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f9ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-plus-lg"></i>
                    Create a new journey
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="journey-selection">
            {/* Only show user's created journeys */}
            <div className="journey-selection-dropdown" style={{ position: 'relative', width: '100%', marginBottom: '16px' }}>
              {journeys.length !== 0 && (<div
                onClick={() => setShowUserJourneyDropdown(!showUserJourneyDropdown)}
                style={{
                  backgroundColor: '#fcfcfd',
                  border: '1px solid #7752ff',
                  borderRadius: '5px',
                  height: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  cursor: 'pointer'
                }}
              >
                <span style={{
                  fontSize: '14px',
                  color: selectedJourney ? '#101828' : '#9ca3af',
                  fontFamily: 'Circular Std, sans-serif',
                  fontWeight: 400
                }}>
                  {selectedJourney ? journeys.find(j => j.id === selectedJourney)?.journeyName : 'Select your user journey'}
                </span>
                <i className={`bi bi-chevron-${showUserJourneyDropdown ? 'up' : 'down'}`} style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}></i>
              </div>)}
                {/* Dropdown Menu */}
              {showUserJourneyDropdown && journeys && journeys.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  zIndex: 1000,
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {journeys.map(journey => (
                    <button
                      key={journey.id}
                      onClick={() => {
                        setSelectedJourney(journey.id);
                        setShowUserJourneyDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: selectedJourney === journey.id ? '#f0f9ff' : 'none',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: selectedJourney === journey.id ? '#1e40af' : '#374151',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        fontWeight: selectedJourney === journey.id ? 600 : 400
                      }}
                      onMouseEnter={(e) => {
                        if (selectedJourney !== journey.id) {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedJourney !== journey.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {journey.journeyName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="create-journey-link"
              onClick={() => {
                setIsCreatingJourney(true);
                setSelectedJourney(null);
              }}
              disabled={isProcessing}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                backgroundColor: 'white',
                border: '1px solid #d0d5dd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: '#101828',
                fontFamily: 'Circular Std, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.borderColor = '#7752ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#d0d5dd';
              }}
            >
              <i className="bi bi-plus-lg"></i>
              <span>Create a new journey</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabs - Show only when journey is selected */}
      {selectedJourney && (
        <div className="session-tabs-container">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k || 'analyze');
              // Reset interaction mode when switching away from feedback tab
              if (k !== 'feedback') {
                // interactionMode removed
              }
            }}
            className="session-tabs"
          >
            <Tab eventKey="analyze" title="Analyze">
              {/* Image Input Section moved here */}
              <div className="image-input-panel" style={{ minHeight: '400px' }}>
                <div className="image-source-section">
                  <h4>Choose Image Source</h4>

                  <button
                    className="capture-screen-btn"
                    onClick={handleCaptureScreen}
                    disabled={isProcessing || !!imageFile || !!imageUrl}
                  >
                    Capture Screen
                  </button>

                  {/* Show captured screen preview */}
                  {screenCapture && (
                    <div className="captured-screen-preview" style={{
                      marginBottom: '16px',
                      padding: '12px',
                      border: '2px solid #7752FF',
                      borderRadius: '8px',
                      backgroundColor: '#f4f2ff'
                    }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong style={{ color: '#7752FF', fontSize: '14px' }}>
                          <i className="bi bi-check-circle-fill me-2"></i>
                          Screen Captured
                        </strong>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setScreenCapture(null)}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Remove
                        </Button>
                      </div>
                      <div style={{ maxHeight: '200px', overflow: 'hidden', borderRadius: '4px' }}>
                        <Image src={screenCapture} fluid style={{ width: '100%' }} />
                      </div>
                    </div>
                  )}

                  <div className="upload-section">
                    <div className="file-upload-area">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={isProcessing || !!imageUrl || !!screenCapture}
                        ref={fileInputRef}
                        className="file-input"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="file-upload-label">
                        Upload screenshot
                      </label>
                      <span className="file-status">
                        {imageFile ? imageFile.name : screenCapture ? 'Screen captured' : 'No file chosen'}
                      </span>
                    </div>

                    <div className="or-divider">Or</div>

                    <div className="url-input-section">
                      <label>Image URL</label>
                      <div className="url-input-wrapper">
                        <input
                          type="text"
                          className="url-input"
                          placeholder="https://example.com"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          disabled={isProcessing || !!imageFile || !!screenCapture}
                        />
                        {imageUrl && (
                          <i className="bi bi-check-circle-fill text-success url-check"></i>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group-modern">
                    <label>What are you looking for in this image? (Optional)</label>
                    <textarea
                      className="form-control-modern textarea"
                      placeholder="Any specific concerns or observations about this image?"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      disabled={isProcessing}
                      rows={3}
                    />
                    {note && (
                      <i className="bi bi-check-circle-fill text-success field-check"></i>
                    )}
                  </div>

                  <div className="toggle-switches">
                    <div className="toggle-item">
                      <Form.Check
                        type="switch"
                        id="fetch-laws-switch"
                        label="Show Legal References"
                        checked={fetchLaws}
                        onChange={(e) => setFetchLaws(e.target.checked)}
                        disabled={isProcessing}
                        className="modern-switch"
                      />
                    </div>
                  </div>

                  <button
                    className="analyze-btn"
                    onClick={handleSubmit}
                    disabled={
                      isProcessing ||
                      (!imageFile && !imageUrl && !screenCapture && imageFiles.length === 0 && imageUrls.length === 0)
                    }
                  >
                    {isProcessing ? (
                      <><Spinner animation="border" size="sm" /> Processing...</>
                    ) : 'Analyze Screen'}
                  </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="conversation" title="Conversation">
              <div className="conversation-container" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '500px'
              }}>
                {/* Messages Area */}
                <div className="chat-messages" ref={chatMessagesRef} style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingBottom: '20px'
                }}>
                  {currentImage ? (
                    messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`message ${msg.type} mx-3 mb-3`}
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #d7d4ff',
                          borderRadius: '10px',
                          padding: '16px 20px',
                          position: 'relative'
                        }}
                      >
                        {renderMessageContent(msg)}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-4 mx-3">
                      <p>Select an image from Previous Scans to view the conversation</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                {/* </div> */}

                {/* Chat Form - Always visible at bottom when there's an image */}
                {currentImage && (
                  <div className="chat-form mx-3 mb-3" style={{
                    backgroundColor: 'white',
                    border: '1px solid #d7d4ff',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    marginTop: 'auto'
                  }}>
                    <Form onSubmit={(e) => {
                      e.preventDefault();
                      handleChatWithImage();
                    }}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Ask a question about this image..."
                          value={chatQuestion}
                          onChange={(e) => setChatQuestion(e.target.value)}
                          disabled={isProcessing}
                          style={{
                            backgroundColor: '#fcfcfd',
                            border: '1px solid #eaecf0',
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '14px',
                            color: '#101828',
                            resize: 'vertical',
                            minHeight: '50px'
                          }}
                        />
                      </Form.Group>
                      <div className="d-flex justify-content-end">
                        <Button
                          type="submit"
                          disabled={isProcessing || !chatQuestion.trim()}
                          size="sm"
                          style={{
                            backgroundColor: '#7752ff',
                            borderColor: '#7752ff',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: 'white',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isProcessing && chatQuestion.trim()) {
                              e.currentTarget.style.backgroundColor = '#6b46c1';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isProcessing && chatQuestion.trim()) {
                              e.currentTarget.style.backgroundColor = '#7752ff';
                            }
                          }}
                        >
                          {isProcessing ? (
                            <><Spinner animation="border" size="sm" /> Processing...</>
                          ) : 'Submit Answer'}
                        </Button>
                      </div>
                    </Form>
                  </div>
                )}
                </div>
              </div>
            </Tab>

            <Tab eventKey="previous-scans" title="Previous Scans">
              <div className="image-history p-3" style={{ minHeight: '400px' }}>
                {journeyImages.length === 0 ? (
                  <p className="text-center text-muted">No images found in this journey</p>
                ) : (
                  <div className="image-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(224px, 1fr))',
                    gap: '26px',
                    padding: '16px 0'
                  }}>
                    {journeyImages.map((image) => (
                      <div
                        key={image.id}
                        className="image-card"
                        onClick={() => handleViewImage(image.id)}
                        style={{
                          width: '224px',
                          height: '290px',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          backgroundColor: 'white',
                          border: '1px solid #7c7e86',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Image */}
                        <div style={{
                          width: '100%',
                          height: '195px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          {image.imageUrl && image.imageUrl.startsWith('data:image') ? (
                            <div className="bg-light d-flex align-items-center justify-content-center h-100">
                              <i className="bi bi-card-image text-muted" style={{ fontSize: '2rem' }}></i>
                            </div>
                          ) : (
                            <Image
                              src={image.imageUrl}
                              fluid
                              style={{
                                objectFit: 'cover',
                                height: '100%',
                                width: '100%'
                              }}
                            />
                          )}
                        </div>

                        {/* Overlay with information */}
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '127px',
                          background: 'linear-gradient(180deg, rgba(37, 40, 54, 0.6) 0%, rgba(37, 40, 54, 0.8) 100%)',
                          backdropFilter: 'blur(7px)',
                          borderRadius: '0 0 10px 10px',
                          padding: '20px 19px',
                          color: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '19px',
                            letterSpacing: '0.3px',
                            marginBottom: '8px'
                          }}>
                            {formatDate(image.createdAt)}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '19px',
                            letterSpacing: '0.3px'
                          }}>
                            {image.url ? `URL: ${image.url.substring(0, 30)}...` : 'URL: No URL provided'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Tab>

            {/* Feedback Tab - Only show when an image is selected */}
            {currentImage && (
              <Tab eventKey="feedback" title="Feedback">
                <div className="feedback-form p-3" style={{
                  backgroundColor: 'white',
                  border: '0.671px solid rgba(0,0,0,0.2)',
                  borderRadius: '4.027px',
                  padding: '24px',
                  position: 'relative',
                  minHeight: '400px'
                }}>
                  <Form onSubmit={(e) => {
                    e.preventDefault();
                    handleRefineOutcome();
                  }}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{
                        fontSize: '16px',
                        fontWeight: 400,
                        color: '#101828',
                        marginBottom: '4px'
                      }}>
                        Give us feedback so we can further improve the analysis
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Explain what might be missing or incorrect in the current analysis"
                        value={refineFeedback}
                        onChange={(e) => setRefineFeedback(e.target.value)}
                        disabled={isProcessing}
                        style={{
                          backgroundColor: '#fcfcfd',
                          border: '1px solid #eaecf0',
                          borderRadius: '8px',
                          padding: '16px',
                          fontSize: '14px',
                          color: '#101828',
                          resize: 'vertical',
                          minHeight: '77px'
                        }}
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      disabled={isProcessing || !refineFeedback.trim()}
                      style={{
                        backgroundColor: '#7752ff',
                        borderColor: '#7752ff',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: 'white',
                        width: '100%',
                        height: '38px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isProcessing && refineFeedback.trim()) {
                          e.currentTarget.style.backgroundColor = '#6b46c1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isProcessing && refineFeedback.trim()) {
                          e.currentTarget.style.backgroundColor = '#7752ff';
                        }
                      }}
                    >
                      {isProcessing ? (
                        <><Spinner animation="border" size="sm" /> Processing...</>
                      ) : 'Send Question'}
                    </Button>
                  </Form>
                </div>
              </Tab>
            )}
          </Tabs>
        </div>
      )}

      {!selectedJourney && (
        <div className="no-journey-selected">
          <p>Please select or create a user journey to continue.</p>
        </div>
      )}

    </div>
  );
};

export default ChatPanel;
